import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Select from 'react-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format, addDays } from 'date-fns';
import type { Supplier, SupplierInvoice } from '@/types/supplier';

const invoiceSchema = z.object({
  supplierId: z.string().min(1, 'Supplier is required'),
  invoiceNumber: z.string().min(1, 'Invoice number is required'),
  date: z.string().min(1, 'Invoice date is required'),
  dueDate: z.string().min(1, 'Due date is required'),
  amount: z.string().refine(val => !isNaN(parseFloat(val)) && parseFloat(val) > 0, {
    message: 'Amount must be a positive number',
  }),
});

type InvoiceForm = z.infer<typeof invoiceSchema>;

interface InvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues?: SupplierInvoice;
  onSubmit: (fd: FormData) => Promise<void>;
  suppliers: Supplier[];
}

export function InvoiceDialog({
  open,
  onOpenChange,
  defaultValues,
  onSubmit,
  suppliers
}: InvoiceDialogProps) {
  const form = useForm<InvoiceForm>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      supplierId: '',
      invoiceNumber: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      dueDate: '',
      amount: '',
    },
  });

  // Reset form when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      form.reset({
        supplierId: defaultValues.supplierId || '',
        invoiceNumber: defaultValues.invoiceNumber || '',
        date: defaultValues.date || format(new Date(), 'yyyy-MM-dd'),
        dueDate: defaultValues.dueDate || '',
        amount: defaultValues.amount?.toString() || '',
      });
    } else {
      form.reset({
        supplierId: '',
        invoiceNumber: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        dueDate: '',
        amount: '',
      });
    }
  }, [defaultValues, form]);

  const { handleSubmit, watch, setValue, control, formState: { errors } } = form;
  const supplierId = watch('supplierId');
  const date = watch('date');
  const [file, setFile] = useState<File | null>(null);

  // Recompute dueDate when supplier or date changes
  useEffect(() => {
    const sup = suppliers.find(s => s.id === supplierId);
    if (sup) {
      const days = sup.paymentTerm === '15days'
        ? 15
        : sup.paymentTerm === '30days'
        ? 30
        : 0;
      setValue('dueDate', format(addDays(new Date(date), days), 'yyyy-MM-dd'));
    }
  }, [supplierId, date, suppliers, setValue]);

  const onFormSubmit = async (data: InvoiceForm) => {
    const fd = new FormData();
    if (defaultValues?.id) {
      fd.append('id', defaultValues.id);
    }
    fd.append('supplier_id', data.supplierId);
    fd.append('invoice_number', data.invoiceNumber);
    fd.append('date', data.date);
    fd.append('due_date', data.dueDate);
    fd.append('amount', data.amount);
    if (file) {
      fd.append('attachment', file);
    }

    await onSubmit(fd);
  };

  // Find the selected supplier
  const selectedSupplier = suppliers.find(s => s.id === supplierId);

  // Options for react-select
  const supplierOptions = suppliers.map(s => ({
    value: s.id,
    label: s.name
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{defaultValues ? 'Edit Invoice' : 'Add Invoice'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium">Supplier</label>
            <Controller
              name="supplierId"
              control={control}
              render={({ field }) => (
                <Select
                  options={supplierOptions}
                  value={supplierOptions.find(option => option.value === field.value)}
                  onChange={(option) => field.onChange(option ? option.value : '')}
                  placeholder="Search for a supplier..."
                  isSearchable
                  className="w-full"
                />
              )}
            />
            {errors.supplierId && <p className="text-red-500 text-sm">{errors.supplierId.message}</p>}
            {selectedSupplier && (
              <p className="text-sm text-gray-600 mt-1">
                Selected: {selectedSupplier.name} (Payment Terms: {selectedSupplier.paymentTerm})
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Invoice Number</label>
            <Controller
              name="invoiceNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
            {errors.invoiceNumber && <p className="text-red-500 text-sm">{errors.invoiceNumber.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Invoice Date</label>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <Input
                    type="date"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
              {errors.date && <p className="text-red-500 text-sm">{errors.date.message}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <Input
                    type="date"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
              {errors.dueDate && <p className="text-red-500 text-sm">{errors.dueDate.message}</p>}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Amount</label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  step="0.01"
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
            {errors.amount && <p className="text-red-500 text-sm">{errors.amount.message}</p>}
          </div>
          <div>
            <label className="text-sm font-medium">Attachment</label>
            <Input
              type="file"
              onChange={(e) => {
                const selectedFile = e.target.files?.[0] || null;
                setFile(selectedFile);
              }}
            />
          </div>
          <DialogFooter>
            <Button type="submit" className="w-full">
              {defaultValues ? 'Update Invoice' : 'Add Invoice'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}